import { Component, Input, OnInit, AfterViewInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  Choice,
  Question,
  Questionnaire,
  FlowRule
} from '@features/opportunities/components/questionnaire-form/models/questionnaire';
import {
  getSelectedAnswers
} from '@features/opportunities/components/questionnaire-form/models/questionnaire-form';
import { FormNamingUtil } from './utils/form-naming.util';
import { 
  Violation, 
  entityHasViolation 
} from '@features/opportunities/modules/core/services/violation.contract';
import { SelectListOption } from '@shared/components/form-fields/select-list-option';
import { setFormControlValue } from '@shared/utilities/form-util';
import { Subscription } from 'rxjs';

type ControlSize = 'sm' | 'lg';

// Typed control definitions for questionnaire (used for individual control creation)
type SingleChoiceControl = FormControl<string | null>;
type MultipleChoiceControl = FormArray<FormControl<boolean>>;
type AdditionalInfoControl = FormControl<string | null>;

// Type for questions with their subquestions
type QuestionWithSubQuestions = {
  question: Question;
  subQuestions: Question[];
};

@Component({
  selector: 'app-questionnaire-form',
  templateUrl: './questionnaire-form.component.html',
  styleUrls: ['./questionnaire-form.component.scss']
})
export class QuestionnaireFormComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() questions!: Question[];
  @Input() flowRules: FlowRule[] = [];
  @Input() editMode = false;
  @Input() questionnaireForm!: FormGroup;
  @Input() size: ControlSize = 'lg';
  @Input() required: boolean = false;
  @Input() violations: Violation[] = [];
  
  @Output() invisibleSubQuestionsChanged = new EventEmitter<Set<string>>();

  get isLg() {
    return this.size == 'lg';
  }
  get isSm() {
    return this.size == 'sm';
  }
  private subscriptions: Subscription[] = [];
  selectOptions: Map<string, SelectListOption[]> = new Map();
  
  // Track which sub-questions should be hidden for each main question (using question numbers)
  invisibleSubQuestions: Set<string> = new Set();
  
  // Map question IDs to form control indices
  questionIdToFormIndex: Map<string, number> = new Map();
  
  // Local variable to store questions with their subquestions
  private questionsWithSubQuestions: QuestionWithSubQuestions[] = [];
  
  // Flag to track if this is the initial load
  private isInitialLoad = true;

  constructor(public formBuilder: FormBuilder) {}

  /**
   * Extracts and organizes subquestions from the questionnaire data
   */
  private extractSubQuestions(): QuestionWithSubQuestions[] {
    const flowRules = this.flowRules;
    const allQuestions = this.questions;
    
    const questionMap = new Map(
      allQuestions.map(q => [q.questionNumber, q])
    );

    const subQuestionNumbers = new Set<number>();
    flowRules.forEach(rule => {
      if (rule.displayType === 'SUB_QUESTION') {
        subQuestionNumbers.add(rule.toQuestionNumber);
      }
    });

    // Filter out subquestions from main questions
    const mainQuestions = allQuestions.filter(q => !subQuestionNumbers.has(q.questionNumber));
    
    // Create the result array with questions and their subquestions
    const questionsWithSubQuestions: QuestionWithSubQuestions[] = mainQuestions.map(mainQuestion => {
      const relatedRules = flowRules.filter(rule => 
        rule.fromQuestionNumber === mainQuestion.questionNumber && 
        rule.displayType === 'SUB_QUESTION'
      );
      
      // Sort rules by groupLabel first, then by displayOrder within each group
      const sortedRules = relatedRules.sort((a, b) => {
        // First sort by groupLabel
        if (a.groupLabel !== b.groupLabel) {
          return a.groupLabel.localeCompare(b.groupLabel);
        }
        // Then sort by displayOrder within the same group
        return a.displayOrder - b.displayOrder;
      });
      
      const subQuestions: Question[] = sortedRules
        .map(rule => questionMap.get(rule.toQuestionNumber) || null)
        .filter((subQuestion): subQuestion is Question => subQuestion !== null)
        .filter((subQuestion, index, array) => 
          array.findIndex(q => q.id === subQuestion.id) === index
        );
      
      return {
        question: mainQuestion,
        subQuestions: subQuestions
      };
    });

    return questionsWithSubQuestions;
  }

  private buildQuestionIdMapping(): void {
    // Map main questions
    this.questionIdToFormIndex = new Map(
      this.questions.map((question, formIndex) => [question.id, formIndex])
    );
  }

  ngOnInit(): void {   
    // Extract and organize subquestions first
    this.questionsWithSubQuestions = this.extractSubQuestions();
       
    // Build mapping of question IDs to form control indices
    this.buildQuestionIdMapping();
    
    this.questionsWithSubQuestions.forEach((question, questionIndex) => {
      // Initialize main question controls with flow rule setup
      this.initializeQuestionControls(question.question, questionIndex, true);
      
      // Initialize subquestion form controls
      this.initializeSubQuestionControls(question);
    });
    this.questionnaireToForm();
  }

  ngAfterViewInit(): void {
    // Evaluate flow rules after view is initialized and form is fully populated
    this.evaluateFlowRulesForAllQuestions(false);
    this.isInitialLoad = false;
    // Emit initial invisibility state after flow rules are evaluated
    this.invisibleSubQuestionsChanged.emit(new Set(this.invisibleSubQuestions));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If questionnaire input changes, re-evaluate flow rules
    if (changes['questionnaire'] && !changes['questionnaire'].firstChange) {
      this.isInitialLoad = true;
      this.questionsWithSubQuestions = this.extractSubQuestions();
      this.buildQuestionIdMapping();
      
      this.questionsWithSubQuestions.forEach((question, questionIndex) => {
        this.initializeQuestionControls(question.question, questionIndex, true);
        this.initializeSubQuestionControls(question);
      });
      this.questionnaireToForm();
      
      // Use setTimeout to ensure form is fully updated
      setTimeout(() => {
        this.evaluateFlowRulesForAllQuestions(false);
        this.isInitialLoad = false;
        this.invisibleSubQuestionsChanged.emit(new Set(this.invisibleSubQuestions));
      }, 0);
    }
  }

  private addSingleChoiceQuestion(
    formControlName: string,
    question: Question
  ) {
    this.questionnaireForm.addControl(
      formControlName,
      new FormControl<string | null>(null, this.required ? [Validators.required] : null) as SingleChoiceControl
    );
    this.selectOptions.set(question.label, this.createSelectOptions(question));
  }
  private addMultipleChoiceQuestion(
    formControlName: string,
    question: Question
  ) {
    let formArray = new FormArray(
      question.options.map((option) => {
        let control = new FormControl<boolean>(false, { nonNullable: true });
        if (option.exclusive) {
          this.subscriptions.push(
            control.valueChanges.subscribe((value) => {
              if (value) {
                this.clearOtherOptions(formControlName, control);
              }
            })
          );
        } else {
          this.subscriptions.push(
            control.valueChanges.subscribe((value) => {
              if (value) {
                this.clearExclusiveOptions(formControlName, question);
              }
            })
          );
        }
        return control;
      })
    );
    this.questionnaireForm.addControl(formControlName, formArray as MultipleChoiceControl);
  }

  /**
   * Gets form control index for a question
   * @param question - The question to get form control index for
   * @returns Form control index
   */
  private getFormControlIndex(question: Question): number {
    return this.questionIdToFormIndex.get(question.id) || 0;
  }

  /**
   * Initializes form controls for a single question
   * @param question - The question to initialize
   * @param questionIndex - Optional fallback index if questionNumber not found in mapping
   * @param setupFlowRules - Whether to setup flow rule subscriptions (default: false)
   */
  private initializeQuestionControls(question: Question, questionIndex?: number, setupFlowRules: boolean = false): void {
    const formControlIndex = this.getFormControlIndex(question) || questionIndex || 0;
    const formControlName = FormNamingUtil.getQuestionName(formControlIndex);
    
    switch (question.questionType) {
      case 'Single Choice':
        this.addSingleChoiceQuestion(
          formControlName,
          question
        );
        break;
      case 'Multiple Choice':
        this.addMultipleChoiceQuestion(
          formControlName,
          question
        );
        break;
    }
    
    this.initialiseAdditionalInfoSubscription(
      this.questionnaireForm.controls[formControlName],
      question
    );

    // Setup flow rule evaluation for questions with subquestions
    if (setupFlowRules && this.questionsWithSubQuestions.some(item => item.question.id === question.id)) {
      this.subscriptions.push(
        this.questionnaireForm.controls[formControlName].valueChanges.subscribe(() => {
          this.evaluateFlowRules(question.id, true);
        })
      );
    }
  }

  private initializeSubQuestionControls(question: QuestionWithSubQuestions): void {
    question.subQuestions.forEach((subQuestion) => {
      this.initializeQuestionControls(subQuestion);
    });
  }

  getAdditionalInformationLabel(question: Question): string {
    return (
      this.getSelectedAnswersRequiringAdditionalInformation(question)[0]
        .freeTextLabel ?? ''
    );
  }


  private createSelectOptions(question: Question): SelectListOption[] {
    return question.options.map((option) => {
      return { value: option.choice, label: option.choice };
    });
  }

  private clearExclusiveOptions(formArrayName: string, question: Question) {
    let formArray = this.questionnaireForm.controls[
      formArrayName
    ] as MultipleChoiceControl;
    formArray.controls
      .filter((_, index) => question.options[index].exclusive)
      .forEach((control) => {
        control.setValue(false);
        control.updateValueAndValidity();
      });
  }

  private clearOtherOptions(
    formArrayName: string,
    formControl: FormControl<boolean>
  ) {
    let formArray = this.questionnaireForm.controls[
      formArrayName
    ] as MultipleChoiceControl;
    formArray.controls
      .filter((x) => x !== formControl)
      .forEach((control) => {
        control.setValue(false);
        control.updateValueAndValidity();
      });
  }

  private initialiseAdditionalInfoSubscription(
    control: AbstractControl,
    question: Question
  ): void {
    this.subscriptions.push(
      control.valueChanges.subscribe((_) => {
        if (this.isAdditionalInformationRequired(question)) {
          const formControlIndex = this.getFormControlIndex(question);
          this.questionnaireForm.addControl(
            FormNamingUtil.getAdditionalInfoName(formControlIndex),
            new FormControl<string | null>(null) as AdditionalInfoControl
          );
          this.questionnaireForm.updateValueAndValidity();
        } else {
          const formControlIndex = this.getFormControlIndex(question);
          this.questionnaireForm.removeControl(FormNamingUtil.getAdditionalInfoName(formControlIndex));
          this.questionnaireForm.updateValueAndValidity();
        }
      })
    );
  }

  isAdditionalInformationRequired(question: Question): boolean {
    return (
      this.getSelectedAnswersRequiringAdditionalInformation(question).length > 0
    );
  }

  private getSelectedAnswersRequiringAdditionalInformation(
    question: Question
  ): Choice[] {
    const allQuestions = this.getAllQuestions();
    const formControlIndex = this.getFormControlIndex(question);
    let control = this.questionnaireForm.controls[FormNamingUtil.getQuestionName(formControlIndex)];
    return getSelectedAnswers(control, allQuestions.indexOf(question), allQuestions).filter(
      (answer) => answer.freeTextLabel != null
    );
  }

  private getAllQuestions(): Question[] {
    return this.questionsWithSubQuestions.flatMap(questionWithSub => [
      questionWithSub.question,
      ...questionWithSub.subQuestions
    ]);
  }

  private questionnaireToForm() {
    this.questionsWithSubQuestions.forEach((question) => {
      this.populateQuestionForm(question.question);
      
      // Also populate subquestions
      question.subQuestions.forEach((subQuestion) => {
        this.populateQuestionForm(subQuestion);
      });
    });
  }

  private populateQuestionForm(question: Question) {
    const formControlIndex = this.getFormControlIndex(question);
    let questionControl =
      this.questionnaireForm.controls[FormNamingUtil.getQuestionName(formControlIndex)];
    let answer = question.answer;
    if (answer != null && answer.choices.length > 0) {
      let choices = answer.choices;
      switch (question.questionType) {
        case 'Single Choice':
          setFormControlValue(
            questionControl as SingleChoiceControl,
            choices[0]
          );
          break;
        case 'Multiple Choice':
          let checkboxes = (questionControl as MultipleChoiceControl).controls;
          question.options.forEach((option, index) => {
            // Find the choice by choice text in the saved choices
            if (choices.includes(option.choice)) {
              checkboxes[index].setValue(true);
              checkboxes[index].updateValueAndValidity();
            } else {
              checkboxes[index].setValue(false);
              checkboxes[index].updateValueAndValidity();
            }
          });
          break;
      }
      questionControl.updateValueAndValidity();
      let freeTextControl =
        this.questionnaireForm.controls[
          FormNamingUtil.getAdditionalInfoName(formControlIndex)
        ];
      if (answer.additionalInformation != null && freeTextControl != null) {
        setFormControlValue(
          freeTextControl as AdditionalInfoControl,
          answer.additionalInformation
        );
        freeTextControl.updateValueAndValidity();
      }
    }
  }

  // Methods that require component state (kept as methods)
  getSelectListOptions(label: string): SelectListOption[] {
    return this.selectOptions.get(label) as SelectListOption[];
  }

  hasViolation(questionId: string): boolean {
    return !!entityHasViolation(this.violations, questionId);
  }

  /**
   * Evaluates flow rules for all questions
   */
  private evaluateFlowRulesForAllQuestions(shouldClearAnswers: boolean = true): void {
    this.questionsWithSubQuestions.forEach(questionWithSubQuestions => {
      this.evaluateFlowRules(questionWithSubQuestions.question.id, shouldClearAnswers);
    });
  }

  /**
   * Evaluates flow rules and updates visible sub-questions
   */
  private evaluateFlowRules(id: string, shouldClearAnswers: boolean = true): void {
    const question = this.questions.find(q => q.id === id);
    const questionWithSubQuestions = this.questionsWithSubQuestions.find(item => item.question.id === id);
    if (!question || !questionWithSubQuestions) return;
    
    const subQuestions = questionWithSubQuestions.subQuestions;

    const formControlIndex = this.getFormControlIndex(question);
    const questionControl = this.questionnaireForm.controls[FormNamingUtil.getQuestionName(formControlIndex)];
    const allQuestions = this.getAllQuestions();
    const questionIndex = allQuestions.indexOf(question);
    
    // During initial load, use the question's answer data directly
    let selectedAnswers: any[] = [];
    if (this.isInitialLoad && question.answer && question.answer.choices && question.answer.choices.length > 0) {
      selectedAnswers = question.options.filter(option => 
        question.answer!.choices.includes(option.choice)
      );
    } else {
      // Use form control value for user interactions
      selectedAnswers = getSelectedAnswers(questionControl, questionIndex, allQuestions);
    }

    // Get currently invisible sub-questions before clearing
    const currentlyInvisibleSubQuestions = new Set(
      subQuestions
        .map(subQuestion => this.invisibleSubQuestions.has(subQuestion.id) ? subQuestion.id : null)
        .filter((id): id is string => id !== null)
    );

    // Clear existing sub-questions for this question
    subQuestions.forEach(subQuestion => {
      this.invisibleSubQuestions.delete(subQuestion.id);
    });

    // Determine which sub-questions should be invisible based on current answers
    const shouldBeInvisible = new Set<string>();
    
    // First, mark all sub-questions as invisible by default
    subQuestions.forEach(subQuestion => {
      shouldBeInvisible.add(subQuestion.id);
    });
    
    selectedAnswers.forEach((selectedChoice) => {
      const matchingChoice = question.options.find(
        option => option.choiceNumber === selectedChoice.choiceNumber
      );

      if (matchingChoice) {
        // Find flow rules that match this choice and show sub-questions
        const applicableRules = this.flowRules.filter((rule: FlowRule) => 
          rule.fromQuestionNumber === question.questionNumber && 
          rule.choiceNumber === matchingChoice.choiceNumber &&
          rule.displayType === 'SUB_QUESTION'
        ) || [];
        
        applicableRules.forEach((rule: FlowRule) => {
          const toQuestion = this.getAllQuestions().find(q => q.questionNumber === rule.toQuestionNumber);
          if (toQuestion) {
            // Remove from invisible set (make it visible)
            shouldBeInvisible.delete(toQuestion.id);
          }
        });
      }
    });

    // Clear answers for sub-questions that are no longer visible (only if not initial load and shouldClearAnswers is true)
    if (shouldClearAnswers && !this.isInitialLoad) {
      subQuestions.forEach(subQuestion => {
        if (!currentlyInvisibleSubQuestions.has(subQuestion.id) && shouldBeInvisible.has(subQuestion.id)) {
          this.clearSubQuestionAnswers(subQuestion);
        }
      });
    }

    // Set the new invisible sub-questions
    [...shouldBeInvisible].map(subQuestionIndex => {
      this.invisibleSubQuestions.add(subQuestionIndex);
    });
    
    
    // Emit the updated invisibility state to parent
    this.invisibleSubQuestionsChanged.emit(new Set(this.invisibleSubQuestions));
  }

  /**
   * Clears answers for a sub-question
   */
  private clearSubQuestionAnswers(subQuestion: Question): void {
    const subFormControlIndex = this.getFormControlIndex(subQuestion);
    const subQuestionControlName = FormNamingUtil.getQuestionName(subFormControlIndex);
    const subQuestionControl = this.questionnaireForm.controls[subQuestionControlName];
    
    if (subQuestionControl) {
      // Clear the main question control
      if (subQuestion.questionType === 'Single Choice') {
        (subQuestionControl as SingleChoiceControl).setValue(null);
      } else if (subQuestion.questionType === 'Multiple Choice') {
        (subQuestionControl as MultipleChoiceControl).controls.forEach(control => {
          control.setValue(false);
        });
      }
      
      // Clear additional information if it exists
      const additionalInfoControlName = FormNamingUtil.getAdditionalInfoName(subFormControlIndex);
      const additionalInfoControl = this.questionnaireForm.controls[additionalInfoControlName];
      if (additionalInfoControl) {
        (additionalInfoControl as AdditionalInfoControl).setValue(null);
        additionalInfoControl.updateValueAndValidity();
      }
      
      // Always try to remove the additional info control to ensure it's cleaned up
      if (this.questionnaireForm.controls[additionalInfoControlName]) {
        this.questionnaireForm.removeControl(additionalInfoControlName);
      }
      
      // Clear the answer in the question object
      if (subQuestion.answer) {
        subQuestion.answer.choices = [];
        subQuestion.answer.additionalInformation = null;
      }
      
      // Update form validity
      subQuestionControl.updateValueAndValidity();
    }
  }


  /**
   * Gets all questions that should be displayed normally (not as sub-questions)
   */
  get mainQuestions(): Question[] {
    return this.questionsWithSubQuestions.map(item => item.question);
  }

  /**
   * Gets sub-questions for a specific parent question
   */
  getSubQuestions(parentQuestionId: string): Question[] {
    const subQuestions = this.questionsWithSubQuestions.find(item => item.question.id === parentQuestionId);
    if (!subQuestions) return [];

    // Filter visible subquestions (not invisible) and maintain the order from the data structure
    // (which is already sorted by groupLabel and displayOrder in the contract mapping)
    return subQuestions.subQuestions.filter(subQuestion => {
      return !this.invisibleSubQuestions.has(subQuestion.id)
    });
  }
}
